import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, IconButton, Stack, Typography } from '@mui/material'
import { EditPencilIcon } from 'Icons/Edit'
import { TrashIcon } from 'Icons/Trash'
import { ConfirmationDialog } from 'Components/Dialog'
import { SectionLoader } from 'Components/Section'
import { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { URL_KEYS } from 'Constants/Url'
import { useKoreanLanguage2, useKoreanSchoolDetail, type SchoolItem } from '../Hooks/useKoreanLanguageForm'
import { KoreanLanguageSchoolEditDialog } from './KoreanLanguageSchoolEditDialog'

const getSchoolTitle = (school: SchoolItem, lang?: string | null) => {
  switch (lang) {
    case 'Uz':
      return school.schoolNameUz
    case 'Kr':
      return school.schoolNameKo
    case 'Ru':
    default:
      return school.schoolNameRu
  }
}

export const KoreanLanguageList = () => {
  const { regions, isRegionsLoading, isRegionsFetching, deleteSchoolMutation } = useKoreanLanguage2()

  const [searchParams] = useSearchParams()
  const lang = searchParams.get(URL_KEYS.LANG)

  const [expandedRegion, setExpandedRegion] = useState<number | null>(null)
  const [removeSchoolId, setRemoveSchoolId] = useState<number | null>(null)
  const [editSchool, setEditSchool] = useState<SchoolItem | null>(null)

  const { data: regionDetail, isLoading: isDetailLoading, isFetching: isDetailFetching } = useKoreanSchoolDetail(expandedRegion)

  const handleAccordionChange = useCallback((regionCode: number) => {
    setExpandedRegion(prev => (prev === regionCode ? null : regionCode))
  }, [])

  const onRemove = useCallback(() => {
    if (!removeSchoolId) return
    deleteSchoolMutation.mutate(removeSchoolId, {
      onSuccess: () => {
        setRemoveSchoolId(null)
      },
    })
  }, [deleteSchoolMutation, removeSchoolId])

  return (
    <>
      <ConfirmationDialog open={!!removeSchoolId} onAccept={onRemove} onClose={() => setRemoveSchoolId(null)} />

      {editSchool && (
        <KoreanLanguageSchoolEditDialog
          school={editSchool}
          open={!!editSchool}
          onClose={() => setEditSchool(null)}
          regionCode={expandedRegion as number}
        />
      )}

      <SectionLoader isLoading={isRegionsLoading} isFetching={isRegionsFetching}>
        <Grid container rowSpacing={2}>
          {regions.map((region: any) => {
            const isExpanded = expandedRegion === region.regionCode
            const detailedSchoolsCount = isExpanded ? (regionDetail?.schools?.length ?? 0) : null

            return (
              <Grid item xs={12} key={region.regionCode}>
                <Accordion expanded={isExpanded} onChange={() => handleAccordionChange(region.regionCode)}>
                  <AccordionSummary>
                    <Stack direction='row' justifyContent='space-between' alignItems='center' width='100%' pr={2}>
                      <Typography fontWeight={700}>{region.regionName}</Typography>
                      <Stack direction='row' spacing={1.5}>
                        {isExpanded && (
                          <Typography variant='body2' color='text.secondary'>
                            학교 수: {detailedSchoolsCount}
                          </Typography>
                        )}
                        <Typography variant='body2' color='text.secondary'>
                          지역 코드: {region.regionCode}
                        </Typography>
                      </Stack>
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails>
                    {isExpanded && (
                      <SectionLoader isLoading={isDetailLoading} isFetching={isDetailFetching}>
                        <Stack spacing={1.5}>
                          {regionDetail?.schools?.length ? (
                            regionDetail.schools.map(school => (
                              <Box
                                key={school.schoolId}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 2,
                                  px: 2,
                                  py: 1.5,
                                }}>
                                <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
                                  <Box>
                                    <Typography fontWeight={600}>{getSchoolTitle(school, lang)}</Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                      ID: {school.schoolId}
                                    </Typography>
                                  </Box>

                                  <Stack direction='row' spacing={1}>
                                    <IconButton onClick={() => setEditSchool(school)}>
                                      <EditPencilIcon />
                                    </IconButton>
                                    <IconButton onClick={() => setRemoveSchoolId(school.schoolId)}>
                                      <TrashIcon />
                                    </IconButton>
                                  </Stack>
                                </Stack>
                              </Box>
                            ))
                          ) : (
                            <Typography color='text.secondary'>등록된 학교가 없습니다.</Typography>
                          )}
                        </Stack>
                      </SectionLoader>
                    )}
                  </AccordionDetails>
                </Accordion>
              </Grid>
            )
          })}
        </Grid>
      </SectionLoader>
    </>
  )
}
